"use client";

import { ExternalLink, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  buildAmazonSearchUrl,
  buildDressSearchTerms,
  normalizeAmazonAssociateTag,
} from "@/lib/designer/amazonSearch";
import type { DesignSpec } from "@/types/designSpec";
import { trackEvent } from "@/utils/analytics";
import { cn } from "@/lib/utils";

export default function FindSimilarDresses({
  designSpec,
  usesSavedPreview,
  placement = "preview",
}: {
  designSpec: DesignSpec | null | undefined;
  usesSavedPreview: boolean;
  placement?: "preview" | "chat_review";
}) {
  const associateTag = normalizeAmazonAssociateTag(
    process.env.NEXT_PUBLIC_AMAZON_ASSOCIATE_TAG
  );
  const searchUrl = buildAmazonSearchUrl(
    buildDressSearchTerms(designSpec),
    associateTag
  );
  if (!searchUrl) return null;

  return (
    <div
      className={cn(
        "designer-similar-search min-w-0",
        placement === "preview" &&
          "flex shrink-0 flex-wrap items-center justify-center gap-x-2 px-4 py-2"
      )}
    >
      <Button
        asChild
        variant={placement === "chat_review" ? "outline" : "link"}
        className={
          placement === "chat_review"
            ? "h-auto min-h-11 w-full min-w-0 justify-start gap-2 rounded-xl border-border bg-card px-3 py-2.5 text-left text-sm font-semibold text-foreground whitespace-normal hover:border-brand-logo/40 hover:bg-muted/35"
            : "h-auto min-h-11 gap-1.5 px-1 py-2 text-xs font-normal text-muted-foreground whitespace-normal"
        }
      >
        <a
          href={searchUrl}
          target="_blank"
          rel={
            associateTag
              ? "sponsored noopener noreferrer"
              : "noopener noreferrer"
          }
          title={
            usesSavedPreview
              ? "Search Amazon using your saved front preview’s details. Results may vary."
              : "Search Amazon using your current design details. Results may vary."
          }
          onClick={() => {
            // Keep search text, photos and customer details out of analytics.
            trackEvent("similar_search_click", {
              retailer: "amazon",
              source: "designer",
              placement,
            });
          }}
        >
          {placement === "chat_review" ? (
            <Search className="size-4" aria-hidden="true" />
          ) : null}
          <span className="min-w-0 flex-1">
            Find similar on Amazon
            {associateTag ? (
              <span className="text-xs font-normal text-muted-foreground">
                {" "}
                (affiliate link)
              </span>
            ) : null}
          </span>
          <ExternalLink className="size-3.5" aria-hidden="true" />
          <span className="sr-only"> (opens in a new tab)</span>
        </a>
      </Button>
    </div>
  );
}
